/**
 * v4.15.0 — Pattern Presets.
 *
 * Quick-start templates that match how operators actually think:
 * "Lunch + Dinner", "Day shift", "Overnight", "All-day Reviews".
 *
 * Each preset returns a complete blocks array that replaces the
 * current schedule. The operator is shown a confirm-replace prompt
 * before any non-empty schedule is overwritten.
 */
import { Sparkles, Clock } from 'lucide-react';

const PRESETS = [
    {
        id: 'lunch_dinner',
        label: 'Lunch + Dinner',
        description: 'Reviews at lunch, Reservations at dinner.',
        build: () => ([
            { start_time: '11:00', end_time: '15:00', days: ['mon','tue','wed','thu','fri','sat','sun'], platform: 'google_reviews' },
            { start_time: '17:00', end_time: '22:00', days: ['mon','tue','wed','thu','fri','sat','sun'], platform: 'instagram' },
        ]),
    },
    {
        id: 'day_shift',
        label: 'Day shift',
        description: 'Google Reviews 9 AM – 5 PM, weekdays only.',
        build: () => ([
            { start_time: '09:00', end_time: '17:00', days: ['mon','tue','wed','thu','fri'], platform: 'google_reviews' },
        ]),
    },
    {
        id: 'weekend_brunch',
        label: 'Weekend brunch',
        description: 'Brunch focus on Sat/Sun, no weekdays.',
        build: () => ([
            { start_time: '10:00', end_time: '15:00', days: ['sat','sun'], platform: 'instagram' },
        ]),
    },
    {
        id: 'overnight',
        label: 'Overnight venue',
        description: '10 PM – 4 AM with crossover, every night.',
        build: () => ([
            { start_time: '22:00', end_time: '04:00', days: ['mon','tue','wed','thu','fri','sat','sun'], platform: 'instagram' },
        ]),
    },
    {
        id: 'all_day_reviews',
        label: 'All-day Reviews',
        description: 'Google Reviews every day, all hours.',
        build: () => ([
            { start_time: '00:00', end_time: '23:59', days: ['mon','tue','wed','thu','fri','sat','sun'], platform: 'google_reviews' },
        ]),
    },
];

export default function PatternPresets({ disabled, hasBlocks, onApply }) {
    const apply = (preset) => {
        if (hasBlocks) {
            const ok = window.confirm(
                `This will replace your current schedule with the "${preset.label}" preset. Continue?`,
            );
            if (!ok) return;
        }
        onApply(preset.build());
    };
    return (
        <div className="space-y-2" data-testid="timing-pattern-presets">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">
                <Sparkles className="w-3 h-3" /> Quick patterns
            </div>
            <div className="flex flex-wrap gap-1.5">
                {PRESETS.map(p => (
                    <button
                        key={p.id}
                        type="button"
                        disabled={disabled}
                        onClick={() => apply(p)}
                        data-testid={`preset-${p.id}`}
                        className="group inline-flex flex-col text-left px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-950/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1">
                            <Clock className="w-3 h-3 text-cyan-500" />
                            {p.label}
                        </span>
                        <span className="text-[10px] text-slate-500 dark:text-slate-400">{p.description}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}
